/**
 * useApi Hook
 * Generic data fetching hook with loading, error, and refresh states.
 * 
 * Usage:
 *   // Immediate fetch
 *   const { data, loading, error, refresh } = useApi(employeeAPI.getAll);
 *   
 *   // Lazy fetch
 *   const { data, execute } = useApi(employeeAPI.getById, { immediate: false });
 *   await execute(employeeId);
 *   
 *   // With callbacks
 *   const { execute } = useApi(employeeAPI.create, {
 *     immediate: false,
 *     onSuccess: () => toast.success('Created!'),
 *     onError: (err) => console.error(err)
 *   });
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';

export function useApi(apiFunc, options = {}) {
  const {
    immediate = true,
    initialData = null,
    onSuccess,
    onError,
    showErrorToast = true,
    deps = []
  } = options;

  const [data, setData] = useState(initialData);
  const [loading, setLoading] = useState(immediate);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  const execute = useCallback(async (...args) => {
    setLoading(true);
    setError(null);

    try {
      const result = await apiFunc(...args);
      
      if (mountedRef.current) {
        setData(result.data);
        onSuccess?.(result.data);
      }
      
      return result.data;
    } catch (err) {
      if (mountedRef.current) {
        setError(err);
        onError?.(err);
        
        if (showErrorToast) {
          const message = err.response?.data?.detail 
            || err.response?.data?.message 
            || err.message 
            || 'An error occurred';
          toast.error(message);
        }
      }
      
      throw err;
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [apiFunc, onSuccess, onError, showErrorToast]);

  useEffect(() => {
    mountedRef.current = true;
    
    if (immediate) {
      execute();
    }

    return () => {
      mountedRef.current = false;
    };
  }, [immediate, ...deps]);

  const refresh = useCallback(() => execute(), [execute]);

  return {
    data,
    setData,
    loading,
    error,
    execute,
    refresh
  };
}

/**
 * useMutation Hook
 * For POST/PUT/DELETE operations with optimistic updates.
 * 
 * Usage:
 *   const { mutate, loading } = useMutation(employeeAPI.create, {
 *     onSuccess: () => refresh()
 *   });
 *   
 *   await mutate(newEmployee);
 */
export function useMutation(apiFunc, options = {}) {
  const {
    onSuccess,
    onError,
    successMessage,
    errorMessage
  } = options;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const mutate = useCallback(async (...args) => {
    setLoading(true);
    setError(null);

    try {
      const result = await apiFunc(...args);
      
      if (successMessage) {
        toast.success(successMessage);
      }
      
      onSuccess?.(result.data);
      return result.data;
    } catch (err) {
      setError(err);
      
      const message = errorMessage 
        || err.response?.data?.detail 
        || 'Operation failed';
      toast.error(message);
      
      onError?.(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [apiFunc, onSuccess, onError, successMessage, errorMessage]);

  return { mutate, loading, error };
}

export default useApi;
